import { AircraftAmenity, AircraftPhoto } from '@shared/components/AircraftOverview';
import { FleetAircraft } from '@shared/components/FleetAircraftList';
import { formatShortDate } from '@shared/utils/formatShortDate';
import { OperatorFileRecord } from '@shared/utils/operatorOverviewProps';

/** The fields of Core's aircraft record the overview reads. Both apps call that endpoint. */
export interface AircraftRecord {
  aircraftName?: string | null;
  name?: string | null;
  tail?: string | null;
  type?: { displayName?: string | null } | null;
  paxCapacity?: number | null;
  operator?: {
    name?: string | null;
    argusRating?: { argusRating?: string | null } | null;
    wyvernRating?: { wyvernRating?: string | null } | null;
  } | null;
  homeBase?: { code?: string | null; displayName?: string | null; airportAddress?: { city?: string | null } | null } | null;
  yearOfMake?: string | number | null;
  yearOfRefurb?: string | number | null;
  aircraftStatus?: boolean | null;
  aircraftNotes?: string | null;
  amenities?: { id: number; amenitiesName?: string | null }[] | null;
  fleet?: { id?: number | null; fleetName?: string | null } | null;
  isFleet?: boolean | null;
  created?: string | null;
  updated?: string | null;
}

export const AVINODE_DOCUMENTS_DISCLAIMER =
  'Avinode takes no responsibility for the validity and authenticity of the documents provided by the '
  + 'aircraft operator. The information is displayed as received from the operator.';

// A fleet has no PAX of its own, so it shows the spread across its member aircraft.
export const formatFleetPax = (aircraft: FleetAircraft[] = []): string => {
  const values = aircraft
    .filter((item) => !item.isFleet)
    .map((item) => item.paxCapacity)
    .filter((pax): pax is number => typeof pax === 'number' && Number.isFinite(pax));

  if (values.length === 0) {
    return 'N/A';
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  return min === max ? `${min}` : `${min}-${max}`;
};

export const toAircraftPhotos = (images?: OperatorFileRecord[] | null): AircraftPhoto[] =>
  (images ?? [])
    .filter((image) => Boolean(image.fullUrl))
    .map((image, index) => ({
      key: image.hash || String(index),
      url: image.fullUrl as string,
      name: image.name,
      isPdf: image.mimeType === 'application/pdf',
    }));

const toAmenities = (amenities?: AircraftRecord['amenities']): AircraftAmenity[] =>
  (amenities ?? []).map((amenity) => ({ id: amenity.id, name: amenity.amenitiesName ?? ' - ' }));

export const toAircraftOverviewProps = (aircraft: AircraftRecord, assignedAircraft: FleetAircraft[] = []) => ({
  aircraftName: aircraft.aircraftName || aircraft.name || '',
  tail: aircraft.tail ?? undefined,
  operatorName: aircraft.operator?.name ?? undefined,
  type: aircraft.type?.displayName ?? undefined,
  paxDisplay: aircraft.isFleet
    ? formatFleetPax(assignedAircraft)
    : (aircraft.paxCapacity === null || aircraft.paxCapacity === undefined ? 'N/A' : String(aircraft.paxCapacity)),
  base: aircraft.homeBase?.displayName
    ? [ aircraft.homeBase.code, aircraft.homeBase.airportAddress?.city ].filter(Boolean).join(', ')
    : undefined,
  yearOfMake: aircraft.yearOfMake ?? undefined,
  yearOfRefurb: aircraft.yearOfRefurb ?? undefined,
  argusRating: aircraft.operator?.argusRating?.argusRating ?? undefined,
  wyvernRating: aircraft.operator?.wyvernRating?.wyvernRating ?? undefined,
  approved: !aircraft.aircraftStatus,
  createdDate: aircraft.created ? formatShortDate(aircraft.created) : undefined,
  updatedDate: aircraft.updated ? formatShortDate(aircraft.updated) : undefined,
  notes: aircraft.aircraftNotes ?? undefined,
  amenities: toAmenities(aircraft.amenities),
  fleetName: aircraft.fleet?.fleetName ?? undefined,
  isFleet: Boolean(aircraft.isFleet),
  assignedAircraft,
});
